// ─────────────────────────────────────────
// ScorecardOverlay.js — multi-player hole-by-hole scorecard
// ─────────────────────────────────────────
// Props:
//   entries       [{id, user_id, guest_name, profiles, hole_scores, score, position, points_earned}]
//   onSave        fn([{id, score, hole_scores, position?, points_earned?}])
//   onClose       fn()
//   title         string
//   showPositions bool  — show position/points section (tournament matchups)
//   round         object {points_1st/2nd/3rd/4th} — needed if showPositions

import { useState } from 'react'
import AvatarImg from './AvatarImg'

function calcPoints(position, round) {
  const pos = parseInt(position)
  if (!pos || !round) return 0
  if (pos === 1) return parseInt(round.points_1st) || 0
  if (pos === 2) return parseInt(round.points_2nd) || 0
  if (pos === 3) return parseInt(round.points_3rd) || 0
  if (pos === 4) return parseInt(round.points_4th) || 0
  return 0
}

function shortName(entry) {
  if (entry.user_id) return `@${entry.profiles?.username || 'P'}`
  return entry.guest_name || 'Guest'
}

const FRONT9 = [0,1,2,3,4,5,6,7,8]
const BACK9  = [9,10,11,12,13,14,15,16,17]

export default function ScorecardOverlay({ entries, onSave, onClose, title, showPositions, round }) {
  const [mode, setMode] = useState('card')
  const [saving, setSaving] = useState(false)

  const [holeInputs, setHoleInputs] = useState(() => {
    const init = {}
    entries.forEach(e => {
      const hs = e.hole_scores || []
      init[e.id] = Array(18).fill('').map((_, i) => hs[i] != null ? String(hs[i]) : '')
    })
    return init
  })

  const [totalInputs, setTotalInputs] = useState(() => {
    const init = {}
    entries.forEach(e => { init[e.id] = e.score != null ? String(e.score) : '' })
    return init
  })

  const [posInputs, setPosInputs] = useState(() => {
    const init = {}
    entries.forEach(e => { init[e.id] = e.position != null ? String(e.position) : '' })
    return init
  })

  function setHole(entryId, idx, val) {
    setHoleInputs(prev => ({
      ...prev,
      [entryId]: prev[entryId].map((v, i) => i === idx ? val : v)
    }))
  }

  function holeSum(entryId, indices) {
    const inputs = holeInputs[entryId] || []
    return indices.filter(i => inputs[i] !== '').reduce((s, i) => s + (parseInt(inputs[i]) || 0), 0)
  }

  function totalFromHoles(entryId) {
    return (holeInputs[entryId] || []).filter(v => v !== '').reduce((s, v) => s + (parseInt(v) || 0), 0)
  }

  function autoRank() {
    const ranked = entries.map(e => {
      const sc = mode === 'card'
        ? (totalFromHoles(e.id) || parseInt(totalInputs[e.id]) || 0)
        : (parseInt(totalInputs[e.id]) || 0)
      return { id: e.id, sc }
    }).sort((a, b) => a.sc - b.sc)
    const next = {}
    ranked.forEach((item, i) => { next[item.id] = String(i + 1) })
    setPosInputs(next)
  }

  async function handleSave() {
    setSaving(true)
    const results = entries.map(e => {
      const pos = posInputs[e.id] ? parseInt(posInputs[e.id]) : null
      const pts = calcPoints(pos, round)
      if (mode === 'card') {
        const hs = (holeInputs[e.id] || []).map(v => v === '' ? null : parseInt(v))
        const total = hs.filter(h => h != null).reduce((s, h) => s + h, 0)
        return { id: e.id, score: total, hole_scores: hs, position: pos, points_earned: pts }
      } else {
        const val = parseInt(totalInputs[e.id])
        return { id: e.id, score: isNaN(val) ? null : val, hole_scores: [], position: pos, points_earned: pts }
      }
    })
    await onSave(results)
    setSaving(false)
  }

  // Column width: fit players across screen, min 56px
  const numPlayers = entries.length
  const colW = Math.max(56, Math.floor((Math.min(window.innerWidth, 520) - 44 - 32) / numPlayers))

  return (
    <div style={sc.overlay}>
      <div style={sc.sheet}>

        {/* Header */}
        <div style={sc.header}>
          <button style={sc.cancelBtn} onClick={onClose}>Cancel</button>
          <div style={sc.headerTitle}>{title || 'Enter Scores'}</div>
          <button style={{ ...sc.saveBtn, opacity: saving ? 0.5 : 1 }} onClick={handleSave} disabled={saving}>
            {saving ? '...' : 'Save'}
          </button>
        </div>

        {/* Mode tabs */}
        <div style={sc.tabs}>
          <button style={{ ...sc.tab, ...(mode === 'card' ? sc.tabActive : {}) }} onClick={() => setMode('card')}>
            🏌️ Scorecard
          </button>
          <button style={{ ...sc.tab, ...(mode === 'total' ? sc.tabActive : {}) }} onClick={() => setMode('total')}>
            # Final Score
          </button>
        </div>

        <div style={sc.body}>

          {/* ── SCORECARD MODE ── */}
          {mode === 'card' && (
            <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}>
              <table style={sc.table}>
                <thead>
                  <tr>
                    <th style={sc.holeHeader}>Hole</th>
                    {entries.map(e => (
                      <th key={e.id} style={{ ...sc.playerHeader, width: colW, minWidth: colW }}>
                        <div style={sc.playerHeaderAvatar}>
                          {e.user_id ? <AvatarImg profile={e.profiles} /> : '👤'}
                        </div>
                        <div style={sc.playerHeaderName}>{shortName(e)}</div>
                        <div style={sc.playerHeaderSub}>
                          {totalFromHoles(e.id) > 0 ? totalFromHoles(e.id) : '—'}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Front 9 */}
                  {FRONT9.map(i => (
                    <tr key={i}>
                      <td style={sc.holeNumCell}>{i + 1}</td>
                      {entries.map(e => (
                        <td key={e.id} style={sc.scoreCell}>
                          <input
                            style={{
                              ...sc.holeInput,
                              background: holeInputs[e.id]?.[i] ? '#e8f5ed' : 'white',
                              borderColor: holeInputs[e.id]?.[i] ? '#2aad56' : '#e8ede9',
                            }}
                            type="number" inputMode="numeric" min={1} max={20}
                            value={holeInputs[e.id]?.[i] || ''}
                            onChange={ev => setHole(e.id, i, ev.target.value)}
                            placeholder="—"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                  {/* OUT */}
                  <tr style={{ background: '#0e1f13' }}>
                    <td style={sc.subTotalLabel}>OUT</td>
                    {entries.map(e => (
                      <td key={e.id} style={sc.subTotalCell}>
                        {holeSum(e.id, FRONT9) || '—'}
                      </td>
                    ))}
                  </tr>
                  {/* Back 9 */}
                  {BACK9.map(i => (
                    <tr key={i}>
                      <td style={sc.holeNumCell}>{i + 1}</td>
                      {entries.map(e => (
                        <td key={e.id} style={sc.scoreCell}>
                          <input
                            style={{
                              ...sc.holeInput,
                              background: holeInputs[e.id]?.[i] ? '#e8f5ed' : 'white',
                              borderColor: holeInputs[e.id]?.[i] ? '#2aad56' : '#e8ede9',
                            }}
                            type="number" inputMode="numeric" min={1} max={20}
                            value={holeInputs[e.id]?.[i] || ''}
                            onChange={ev => setHole(e.id, i, ev.target.value)}
                            placeholder="—"
                          />
                        </td>
                      ))}
                    </tr>
                  ))}
                  {/* IN */}
                  <tr style={{ background: '#0e1f13' }}>
                    <td style={sc.subTotalLabel}>IN</td>
                    {entries.map(e => (
                      <td key={e.id} style={sc.subTotalCell}>
                        {holeSum(e.id, BACK9) || '—'}
                      </td>
                    ))}
                  </tr>
                  {/* TOTAL */}
                  <tr style={{ background: '#061008' }}>
                    <td style={{ ...sc.subTotalLabel, color: '#c8f135', fontSize: 10, letterSpacing: 1 }}>TOT</td>
                    {entries.map(e => (
                      <td key={e.id} style={{ ...sc.subTotalCell, color: '#c8f135', fontSize: 20, fontWeight: 900 }}>
                        {totalFromHoles(e.id) || '—'}
                      </td>
                    ))}
                  </tr>
                </tbody>
              </table>
            </div>
          )}

          {/* ── FINAL SCORE MODE ── */}
          {mode === 'total' && (
            <div style={sc.finalList}>
              {entries.map(e => (
                <div key={e.id} style={sc.finalRow}>
                  <div style={sc.finalAvatar}>
                    {e.user_id ? <AvatarImg profile={e.profiles} /> : '👤'}
                  </div>
                  <div style={sc.finalName}>{shortName(e)}</div>
                  <input
                    style={sc.finalInput}
                    type="number" inputMode="numeric" min={0} max={200}
                    value={totalInputs[e.id] || ''}
                    onChange={ev => setTotalInputs(prev => ({ ...prev, [e.id]: ev.target.value }))}
                    placeholder="—"
                    autoFocus={e === entries[0]}
                  />
                </div>
              ))}
            </div>
          )}

          {/* ── POSITIONS (tournament only) ── */}
          {showPositions && (
            <div style={sc.posSection}>
              <div style={sc.posSectionHeader}>
                <div style={sc.posSectionTitle}>POSITIONS & POINTS</div>
                <button style={sc.autoRankBtn} onClick={autoRank}>Auto-rank</button>
              </div>
              {round && (
                <div style={sc.ptsReminder}>
                  {['1st','2nd','3rd','4th'].map((pl, pi) => {
                    const fields = ['points_1st','points_2nd','points_3rd','points_4th']
                    return (
                      <div key={pl} style={sc.ptsPill}>
                        <div style={sc.ptsPillNum}>{round[fields[pi]] ?? '—'}</div>
                        <div style={sc.ptsPillLabel}>{pl}</div>
                      </div>
                    )
                  })}
                </div>
              )}
              {entries.map(e => {
                const pos = posInputs[e.id] ? parseInt(posInputs[e.id]) : null
                const pts = calcPoints(pos, round)
                return (
                  <div key={e.id} style={sc.posRow}>
                    <div style={sc.posAvatar}>
                      {e.user_id ? <AvatarImg profile={e.profiles} /> : '👤'}
                    </div>
                    <div style={sc.posName}>{shortName(e)}</div>
                    <select
                      style={sc.posSelect}
                      value={posInputs[e.id] || ''}
                      onChange={ev => setPosInputs(prev => ({ ...prev, [e.id]: ev.target.value }))}>
                      <option value="">Place —</option>
                      {entries.map((_, i) => (
                        <option key={i + 1} value={String(i + 1)}>{i + 1}{['st','nd','rd','th'][Math.min(i,3)]}</option>
                      ))}
                    </select>
                    <div style={{
                      ...sc.ptsBadge,
                      background: pts > 0 ? '#0e1f13' : '#f4f7f5',
                      color: pts > 0 ? '#c8f135' : '#9aaa9d'
                    }}>
                      {pts > 0 ? `+${pts}` : '—'}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

        </div>
      </div>
    </div>
  )
}

const sc = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.6)',
    zIndex: 300,
    display: 'flex', alignItems: 'flex-end', justifyContent: 'center'
  },
  sheet: {
    background: '#f4f7f5',
    width: '100%', maxWidth: 560,
    borderRadius: '20px 20px 0 0',
    maxHeight: '94vh',
    display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 -8px 40px rgba(0,0,0,0.25)'
  },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '16px 18px 14px', background: '#0e1f13', flexShrink: 0
  },
  cancelBtn: {
    background: 'none', border: 'none', color: 'rgba(255,255,255,0.55)',
    fontSize: 14, fontWeight: 600, cursor: 'pointer',
    fontFamily: "'Barlow', sans-serif", padding: 0
  },
  headerTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 17, color: 'white', letterSpacing: 0.5
  },
  saveBtn: {
    background: '#c8f135', color: '#0e1f13', border: 'none',
    borderRadius: 8, padding: '7px 18px', fontWeight: 800,
    fontSize: 14, cursor: 'pointer', fontFamily: "'Barlow Condensed', sans-serif"
  },
  tabs: {
    display: 'flex', background: 'white',
    borderBottom: '1.5px solid #f0f4f1', flexShrink: 0
  },
  tab: {
    flex: 1, background: 'none', border: 'none', padding: '12px 4px',
    fontSize: 13, fontWeight: 700, color: '#6b7c6e', cursor: 'pointer',
    fontFamily: "'Barlow', sans-serif",
    borderBottom: '2.5px solid transparent', marginBottom: -2
  },
  tabActive: { color: '#0e1f13', borderBottom: '2.5px solid #c8f135' },
  body: { flex: 1, overflowY: 'auto', overflowX: 'hidden' },

  // Table
  table: { width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' },

  holeHeader: {
    background: '#0e1f13', color: 'rgba(255,255,255,0.5)',
    width: 44, padding: '10px 0', textAlign: 'center',
    fontSize: 9, fontWeight: 800, letterSpacing: 1, textTransform: 'uppercase',
    fontFamily: "'Barlow Condensed', sans-serif", position: 'sticky', left: 0, zIndex: 2
  },
  playerHeader: {
    background: '#0e1f13', color: 'white',
    padding: '8px 4px', textAlign: 'center',
    borderLeft: '1px solid rgba(255,255,255,0.08)'
  },
  playerHeaderAvatar: {
    width: 28, height: 28, borderRadius: '50%', fontSize: 14,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    margin: '0 auto 4px', overflow: 'hidden'
  },
  playerHeaderName: {
    fontSize: 10, fontWeight: 800, color: 'white',
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
    fontFamily: "'Barlow Condensed', sans-serif", letterSpacing: 0.3
  },
  playerHeaderSub: {
    fontSize: 14, fontWeight: 900, color: '#c8f135',
    fontFamily: "'Barlow Condensed', sans-serif", lineHeight: 1.2, marginTop: 2
  },

  holeNumCell: {
    background: '#0e1f13', color: 'rgba(255,255,255,0.7)',
    width: 44, height: 44, textAlign: 'center',
    fontSize: 12, fontWeight: 800,
    fontFamily: "'Barlow Condensed', sans-serif",
    borderBottom: '1px solid rgba(255,255,255,0.07)',
    position: 'sticky', left: 0, zIndex: 1
  },
  scoreCell: {
    padding: 0, borderBottom: '1px solid #f0f4f1',
    borderLeft: '1px solid #f0f4f1', background: 'white'
  },
  holeInput: {
    width: '100%', height: 44, border: '2px solid #e8ede9',
    textAlign: 'center', fontSize: 16, fontWeight: 800,
    fontFamily: "'Barlow Condensed', sans-serif",
    color: '#0e1f13', outline: 'none', background: 'white',
    MozAppearance: 'textfield',
    WebkitAppearance: 'none',
    boxSizing: 'border-box'
  },
  subTotalLabel: {
    background: '#0e1f13', color: 'rgba(255,255,255,0.5)',
    width: 44, height: 38, textAlign: 'center',
    fontSize: 9, fontWeight: 800, letterSpacing: 1,
    textTransform: 'uppercase', verticalAlign: 'middle',
    fontFamily: "'Barlow Condensed', sans-serif",
    position: 'sticky', left: 0, zIndex: 1
  },
  subTotalCell: {
    background: '#0e1f13', color: 'rgba(255,255,255,0.85)',
    textAlign: 'center', fontSize: 16, fontWeight: 900,
    fontFamily: "'Barlow Condensed', sans-serif",
    borderLeft: '1px solid rgba(255,255,255,0.1)'
  },

  // Final score mode
  finalList: { padding: '16px', display: 'flex', flexDirection: 'column', gap: 12 },
  finalRow: {
    display: 'flex', alignItems: 'center', gap: 12,
    background: 'white', borderRadius: 14, padding: '12px 16px',
    border: '1.5px solid #e2ebe4'
  },
  finalAvatar: {
    width: 40, height: 40, borderRadius: '50%', fontSize: 20,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, overflow: 'hidden'
  },
  finalName: {
    flex: 1, fontWeight: 700, fontSize: 14, color: '#0e1f13',
    fontFamily: "'Barlow', sans-serif"
  },
  finalInput: {
    width: 80, background: '#f4f7f5', border: '2px solid #0e1f13',
    borderRadius: 12, padding: '10px 8px', fontSize: 28, fontWeight: 900,
    color: '#0e1f13', outline: 'none', textAlign: 'center',
    fontFamily: "'Barlow Condensed', sans-serif",
    MozAppearance: 'textfield', WebkitAppearance: 'none'
  },

  // Positions section
  posSection: {
    background: 'white', borderTop: '2px solid #f0f4f1', marginTop: 4
  },
  posSectionHeader: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: '12px 16px 8px'
  },
  posSectionTitle: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 11, color: '#0e1f13', letterSpacing: 2
  },
  autoRankBtn: {
    background: '#e8f5ed', color: '#1a7a3c', border: 'none',
    borderRadius: 8, padding: '5px 12px', fontSize: 11, fontWeight: 700,
    cursor: 'pointer', fontFamily: "'Barlow', sans-serif"
  },
  ptsReminder: {
    display: 'flex', background: '#0e1f13', margin: '0 16px 12px',
    borderRadius: 10, padding: '8px 0'
  },
  ptsPill: { flex: 1, textAlign: 'center' },
  ptsPillNum: {
    fontFamily: "'Barlow Condensed', sans-serif",
    fontWeight: 900, fontSize: 18, color: '#c8f135', lineHeight: 1
  },
  ptsPillLabel: { fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: 0.5, marginTop: 2 },
  posRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 16px', borderTop: '1px solid #f0f4f1'
  },
  posAvatar: {
    width: 32, height: 32, borderRadius: '50%', fontSize: 16,
    background: 'linear-gradient(135deg,#2aad56,#1a7a3c)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    flexShrink: 0, overflow: 'hidden'
  },
  posName: { flex: 1, fontWeight: 700, fontSize: 13, color: '#0e1f13' },
  posSelect: {
    background: 'white', border: '1.5px solid #e2ebe4',
    borderRadius: 8, padding: '6px 10px', fontSize: 13, fontWeight: 600,
    color: '#0e1f13', outline: 'none', flexShrink: 0,
    fontFamily: "'Barlow', sans-serif"
  },
  ptsBadge: {
    borderRadius: 20, padding: '4px 10px', fontSize: 11, fontWeight: 800,
    fontFamily: "'Barlow Condensed', sans-serif", flexShrink: 0
  }
}
